import React, { useState, useEffect } from "react";
import { database } from "../firebaseConfig";
import { ref, onValue } from "firebase/database";
import "./WhisperAnalytics.css";

const WhisperAnalytics = ({ userId }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountStats, setAccountStats] = useState({});
  const [maxAccountNumber, setMaxAccountNumber] = useState(0);

  // Load profiles from database
  useEffect(() => {
    if (!userId) return;

    const profilesRef = ref(database, `/Whisper/${userId}/db/profiles`);
    const unsubscribe = onValue(profilesRef, (snapshot) => {
      if (snapshot.exists()) {
        const profilesData = [];
        snapshot.forEach((childSnapshot) => {
          profilesData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setProfiles(profilesData);
        
        // Find highest account number
        const highestAccountNumber = Math.max(
          ...profilesData
            .filter(profile => profile.accountNumber !== undefined)
            .map(profile => profile.accountNumber),
          0
        );
        setMaxAccountNumber(highestAccountNumber);
        
        // Calculate statistics
        calculateAccountStats(profilesData, highestAccountNumber);
      } else {
        setProfiles([]);
        setAccountStats({});
        setMaxAccountNumber(0);
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [userId]);

  // Calculate statistics for each account number
  const calculateAccountStats = (profilesData, maxNumber) => {
    const stats = {};
    
    // Initialize statistics for each account number from 1 to maxNumber
    for (let i = 1; i <= maxNumber; i++) {
      stats[i] = {
        total: 0,
        fresh: 0,
        other: 0,
        profiles: []
      };
    }
    
    // Count profiles for each account number
    profilesData.forEach(profile => {
      const accountNumber = profile.accountNumber;
      
      if (accountNumber && accountNumber > 0 && accountNumber <= maxNumber) {
        stats[accountNumber].total += 1;
        stats[accountNumber].profiles.push(profile);
        
        if (profile.status === "fresh") {
          stats[accountNumber].fresh += 1;
        } else {
          stats[accountNumber].other += 1;
        }
      }
    });
    
    setAccountStats(stats);
  };

  // Calculate total statistics
  const calculateTotalStats = () => {
    const numbers = Object.keys(accountStats).map(Number);
    
    if (numbers.length === 0) {
      return { total: 0, fresh: 0, other: 0 };
    }
    
    return numbers.reduce(
      (acc, num) => {
        acc.total += accountStats[num].total;
        acc.fresh += accountStats[num].fresh;
        acc.other += accountStats[num].other;
        return acc;
      },
      { total: 0, fresh: 0, other: 0 }
    );
  };

  // Calculate percentage for progress bars
  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return (value / total) * 100;
  };

  const totalStats = calculateTotalStats();

  return (
    <div className="whisper-analytics-container">
      <h3 className="whisper-analytics-title">Whisper Profile Analysis</h3>
      
      {loading ? (
        <p className="whisper-analytics-loading">Loading data...</p>
      ) : profiles.length === 0 ? (
        <p className="whisper-analytics-empty">No profiles available for analysis.</p>
      ) : (
        <>
          <div className="whisper-analytics-summary">
            <div className="whisper-analytics-summary-item">
              <h4>Overall Statistics</h4>
              <p>Total: <strong>{totalStats.total}</strong> profiles</p>
              <p>Fresh: <strong>{totalStats.fresh}</strong> profiles</p>
              <p>Other statuses: <strong>{totalStats.other}</strong> profiles</p>
              
              <div className="whisper-analytics-total-progress">
                <div className="whisper-analytics-progress-container">
                  <div 
                    className="whisper-analytics-progress-bar whisper-analytics-progress-fresh" 
                    style={{ width: `${calculatePercentage(totalStats.fresh, totalStats.total)}%` }}
                  ></div>
                  <div 
                    className="whisper-analytics-progress-bar whisper-analytics-progress-other" 
                    style={{ width: `${calculatePercentage(totalStats.other, totalStats.total)}%` }}
                  ></div>
                </div>
                <div className="whisper-analytics-progress-legend">
                  <span className="whisper-analytics-legend-item">
                    <span className="whisper-analytics-legend-color whisper-analytics-legend-fresh"></span>
                    Fresh
                  </span>
                  <span className="whisper-analytics-legend-item">
                    <span className="whisper-analytics-legend-color whisper-analytics-legend-other"></span>
                    Other
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="whisper-analytics-accounts">
            <h4 className="whisper-analytics-subtitle">Status by Account Number (1-{maxAccountNumber})</h4>
            
            <div className="whisper-analytics-grid">
              {Object.keys(accountStats).map(accountNumber => {
                const stat = accountStats[accountNumber];
                if (stat.total === 0) return null;
                
                const freshPercentage = calculatePercentage(stat.fresh, stat.total);
                const otherPercentage = calculatePercentage(stat.other, stat.total);
                
                return (
                  <div key={accountNumber} className="whisper-analytics-account-card">
                    <h5 className="whisper-analytics-account-number">Account #{accountNumber}</h5>
                    <div className="whisper-analytics-account-stats">
                      <p>Total: <strong>{stat.total}</strong></p>
                      <p>Fresh: <strong>{stat.fresh}</strong></p>
                      <p>Other: <strong>{stat.other}</strong></p>
                    </div>
                    
                    <div className="whisper-analytics-progress-container">
                      <div 
                        className="whisper-analytics-progress-bar whisper-analytics-progress-fresh" 
                        style={{ width: `${freshPercentage}%` }}
                      ></div>
                      <div 
                        className="whisper-analytics-progress-bar whisper-analytics-progress-other" 
                        style={{ width: `${otherPercentage}%` }}
                      ></div>
                    </div>
                    
                    <div className="whisper-analytics-percentage-labels">
                      <span>{Math.round(freshPercentage)}% Fresh</span>
                      <span>{Math.round(otherPercentage)}% Other</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WhisperAnalytics; 